import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightbox component for displaying images and videos in a modal
 * @param {string} mediaUrl - URL of the media to display
 * @param {string} mediaType - 'image' or 'video'
 * @param {Function} onClose - Function to call when closing
 * @param {Array} allMedia - Array of all media items for navigation
 * @param {number} currentIndex - Current media index
 * @param {Function} onNavigate - Function to navigate between media
 */
export const Lightbox = ({ 
  mediaUrl, 
  mediaType = 'image', 
  onClose, 
  allMedia = [], 
  currentIndex = 0, 
  onNavigate 
}) => {
  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < allMedia.length - 1) onNavigate(currentIndex + 1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, currentIndex, allMedia.length, onNavigate]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const isLikelyVideo = mediaUrl && (
    mediaUrl.endsWith('.mp4') || 
    mediaUrl.endsWith('.gifv') || 
    mediaUrl.endsWith('.webm')
  );

  const showNavigation = allMedia.length > 1;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
        aria-label="Cerrar"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Navigation Buttons */}
      {showNavigation && currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>
      )}

      {showNavigation && currentIndex < allMedia.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          aria-label="Siguiente"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Media Content */}
      <div 
        className="max-w-7xl max-h-[90vh] w-full px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {isLikelyVideo || mediaType === 'video' ? (
          <video
            src={mediaUrl}
            controls
            autoPlay
            loop
            className="w-full h-full max-h-[90vh] object-contain rounded-lg"
          >
            Tu navegador no soporta el elemento de video.
          </video>
        ) : (
          <img
            src={mediaUrl}
            alt="Vista ampliada"
            className="w-full h-full max-h-[90vh] object-contain rounded-lg"
          />
        )}
      </div>

      {/* Counter */}
      {showNavigation && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
          {currentIndex + 1} / {allMedia.length}
        </div>
      )}
    </div>
  );
};
